import React, { useMemo, useState } from 'react';
import { useSearchParams, Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import SystemsHeader from '../../components/SystemsHeader';
import SystemsFooter from '../../components/SystemsFooter';
import SeoHead from '../../components/SeoHead';
import { useSystemsCatalog } from '../../context/SystemsCatalogContext';
import { useCurrency } from '../../context/CurrencyContext';
import { Check, ShieldCheck, Lock, ArrowRight } from 'lucide-react';

const API = `${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api`;

export default function PaymentCheckout() {
  const [params] = useSearchParams();
  const slug = params.get('slug');
  const tierId = params.get('tier');
  const { getService, getTier, loading } = useSystemsCatalog();
  const { format, info: currencyInfo } = useCurrency();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const svc = useMemo(() => getService(slug), [getService, slug]);
  const tier = useMemo(() => getTier(slug, tierId), [getTier, slug, tierId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <SystemsHeader />
        <main className="py-32 text-center text-gray-500">Loading checkout…</main>
        <SystemsFooter />
      </div>
    );
  }
  if (!svc || !tier) return <Navigate to="/systems/solutions" replace />;

  const handlePayByCard = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/systems/checkout`, {
        slug,
        tier_id: tierId,
        origin_url: window.location.origin,
        email: email || null,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error('Checkout URL missing');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Could not create checkout session');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SeoHead path="/systems/payment" title={`Checkout — ${svc.name}`} description={`Complete your ${tier.name} subscription for ${svc.name}.`} />
      <SystemsHeader />
      <main className="py-16 md:py-20 bg-[#fafafa] min-h-[70vh]">
        <div className="zh-container max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px] gap-8">
            {/* LEFT — secure card form */}
            <div className="rounded-2xl bg-white border border-gray-200 p-6 md:p-8">
              <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900">Complete your purchase</h1>
              <p className="mt-2 text-gray-600 text-[15px]">Pay securely with card to activate {svc.name}.</p>

              <form onSubmit={handlePayByCard} className="mt-6 space-y-4" data-testid="pay-card-form">
                <div>
                  <label className="block text-[12px] font-semibold uppercase tracking-wider text-gray-600 mb-1.5">Email for receipt</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="pay-card-email"
                    placeholder="you@company.com"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:border-[#f5a623] focus:ring-2 focus:ring-amber-100 outline-none transition"
                  />
                </div>
                {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="pay-card-error">{error}</div>}
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="pay-card-submit"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#f5a623] text-black text-[14px] font-bold uppercase tracking-wider hover:brightness-110 transition disabled:opacity-60"
                >
                  {submitting ? 'Redirecting…' : <>Proceed to Secure Checkout <ArrowRight className="w-4 h-4" /></>}
                </button>
                <p className="text-center text-[11px] text-gray-500 flex items-center justify-center gap-1.5"><Lock className="w-3 h-3" /> Secure card payment by Stripe</p>
              </form>
            </div>

            {/* RIGHT — order summary */}
            <aside className="rounded-2xl bg-white border border-gray-200 p-6 h-fit lg:sticky lg:top-32" data-testid="order-summary">
              <h2 className="text-[13px] font-extrabold uppercase tracking-[0.18em] text-gray-500">Order summary</h2>
              <div className="mt-4 pb-4 border-b border-gray-100">
                <div className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#c9a227]">{svc.category}</div>
                <div className="mt-1 text-base font-bold text-gray-900">{svc.name}</div>
                <div className="mt-0.5 text-[13px] text-gray-500">{tier.name} plan</div>
              </div>
              <ul className="mt-4 space-y-2">
                {tier.features.slice(0, 4).map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-[13px] text-gray-700">
                    <Check className="w-3.5 h-3.5 text-[#f5a623] mt-1 flex-shrink-0" strokeWidth={3} />
                    <span>{f}</span>
                  </li>
                ))}
                {tier.features.length > 4 && (
                  <li className="text-[12px] text-gray-500 pl-5">+ {tier.features.length - 4} more</li>
                )}
              </ul>
              <div className="mt-5 pt-5 border-t border-gray-100 flex items-end justify-between">
                <div>
                  <div className="text-[12px] text-gray-500">{svc.billing === 'subscription' ? 'Monthly' : 'Total'}</div>
                  <div className="text-3xl font-extrabold text-gray-900">{format(Math.round(tier.price))}</div>
                </div>
                <div className="text-[12px] text-gray-500 mb-1">{currencyInfo.code} /{svc.unit}</div>
              </div>
              <div className="mt-5 rounded-xl bg-amber-50 border border-amber-100 p-3 text-[12px] text-amber-900 leading-relaxed flex gap-2">
                <ShieldCheck className="w-4 h-4 text-[#c9a227] mt-0.5 flex-shrink-0" />
                {svc.billing === 'subscription' ? 'Monthly billing, cancel anytime. 14-day money-back guarantee.' : 'One-time payment. Refundable before kick-off call.'}
              </div>
              <div className="mt-4 text-[12px]">
                <Link to={`/systems/solutions/${slug}/pricing`} className="text-gray-500 hover:text-black">← Change plan</Link>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <SystemsFooter />
    </div>
  );
}
